import '../../modules/mode-components';

import { Compiler } from '@mode-switch/adapt';
import { Model } from '@mode-switch/model';
import * as angular from 'angular';
import { fromEvent } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';

import { MixableBaseController } from '~/common/component';
import { FlamingoTypes } from '@mode/shared/contract-common';

import { Switch } from '@mode/shared/util-switch';
import { formatSignature, makeAccessCompleter } from './formula-editor-completers';
import { OnChangeEvent, OnCompileEvent } from './formula-editor-events';
import { configureCustomizeCompletion } from '../../plugins/ace/custom-popup';

const Range = ace.require('ace/range').Range;

class FormulaEditorController extends MixableBaseController {
  static $inject = ['$scope', '$element', 'ace'];

  // Data Bindings
  private viewName: string;
  private readOnly?: boolean;
  private showRenamingWarning: boolean;
  private calc: FlamingoTypes.ModeCalc;
  private model: FlamingoTypes.ModeModel;

  // Event Bindings
  private onChange: (args: { $event: OnChangeEvent }) => void;
  private onCompile: (args: { $event: OnCompileEvent }) => void;

  // Binding Derived State
  private view: FlamingoTypes.ModeView;

  // Local UI State
  private editor: AceAjax.Editor;
  private errorMarker: any;
  // Explor*ing*, or an Exploration
  private isExploreish: boolean;
  private isImportedDataset: boolean;
  private showBanner: boolean;

  constructor(
    private $scope: angular.IScope,
    private $element: angular.IAugmentedJQuery,
    private ace: AceAjax.Ace
  ) {
    super();
  }

  compile(formula: string) {
    if (formula != null && formula.length > 0) {
      const result = Switch.compile(this.model, this.view.name, formula);

      this.$scope.$evalAsync(() => {
        this.updateMarkers(result);

        // Fire Events
        this.onCompile({ $event: { result, formula } });
      });
    }
  }

  $onInit() {
    this.showBanner = this.isExploreish || this.showRenamingWarning || this.isImportedDataset;
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.model || changes.viewName) {
      if (this.model != null && this.viewName != null) {
        this.view = Model.Views.find(this.model, this.viewName);
      }
    }

    if (changes.showRenamingWarning && !this.isExploreish) {
      this.showBanner = this.showRenamingWarning;
    }

    if (changes.formulaSignature && changes.formulaSignature.currentValue) {
      const formattedSignature = formatSignature(changes.formulaSignature.currentValue);
      this.editor.insert(formattedSignature);
    }
  }

  $postLink() {
    const container = this.$element.find('.editor').get(0);
    this.editor = this.ace.edit(container);

    // remove deprecation warning
    this.editor.$blockScrolling = Infinity;
    this.editor.renderer.$fontMetrics.setPolling(false);
    this.editor.renderer.setScrollMargin(6, 6, 6, 6);

    this.editor.container.style.fontSize = '12px';
    this.editor.container.style.lineHeight = '18px';

    /**
     * Need to explicitly set this on the session when
     * the mode is javascript.. otherwise the browser
     * throws the following error ↓
     * ---
     * Access Denied / Failed to contruct Worker: CSP -> DOMException
     */
    this.editor.getSession().setUseWorker(false);

    this.editor.setOptions({
      /**
       * @todo: Why is this not sql|modesql ?
       */
      mode: 'ace/mode/javascript',
      tabSize: 2,
      useWorker: false,
      showGutter: false,
      showPrintMargin: false,
      highlightActiveLine: false,
      enableBasicAutocompletion: !this.readOnly,
      placeholder: 'Type a formula, like SUM([amount])/COUNT([id]) \n// You can also add comments in your formula',
    });

    // Initial Value
    if (this.calc != null) {
      this.editor.setValue(this.getCalcFormula(), 1);
    }

    if (this.readOnly) {
      this.editor.setReadOnly(true);
      this.editor.blur();
    } else {
      this.compile(this.editorContent);

      /// Live Compilation
      fromEvent(this.editor, 'change')
        .pipe(
          tap(() => {
            this.$scope.$evalAsync(() => {
              this.clearMarkers();
              // Fire change event immediately
              this.onChange({
                $event: {
                  formula: this.editorContent,
                },
              });
            });
          }),
          debounceTime(500),
          takeUntil(this.destroyed$)
        )
        .subscribe(() => {
          this.compile(this.editorContent);
        });

      this.editor.preferences = {
        autocomplete_mode: 'ms',
        autocomplete_mode_threshold: 4,
      };

      // needed to trigger custom pop for autocomplete that includes icons
      // and also to add class to prevent clashing with primary SQL editor
      // autocomplete styles
      this.editor.commands.on('afterExec', configureCustomizeCompletion(this.ace));
      this.editor.commands.on('afterExec', (e) => {
        const completer = e.editor.completer;
        if (completer) {
          completer.popup.container.classList.add('ace-calc-fields-completion');
          completer.popup.container.style.lineHeight = '24px';
        }
      });

      this.editor.completers = [];
      this.editor.completers.push(makeAccessCompleter(this.model, this.getViewName()));

      /// Focus Editor
      this.editor.focus();
    }
  }

  // Getters

  get editorContent() {
    return this.editor.getValue() as string;
  }

  getViewName() {
    return this.view == null ? null : this.view.name;
  }

  getCalcFormula(): string {
    return this.calc == null ? null : this.calc.formula.source;
  }

  // Setters

  clearMarkers() {
    if (this.errorMarker != null) {
      this.editor.getSession().removeMarker(this.errorMarker);
    }
  }

  updateMarkers(result: FlamingoTypes.ModeCompilerResult): void {
    switch (result.type) {
      case Compiler.Result.Type.ParseFailure: {
        this.addParseErrorMarker(result.errors[0]);
        break;
      }
      case Compiler.Result.Type.ResolveFailure: {
        break;
      }
    }
  }

  addParseErrorMarker(error: any) {
    if (error.hasOwnProperty('location')) {
      const endRow = error.location.end.line - 1;
      const endCol = error.location.end.column - 1;
      let startCol = error.location.start.column - 1;
      const startRow = error.location.start.line - 1;

      if (endCol - startCol === 0) {
        startCol = endCol - 1;
      }

      const range = new Range(startRow, startCol, endRow, endCol);
      this.editor.getSession().doc.createAnchor(endRow, range.end);
      this.editor.getSession().doc.createAnchor(startRow, range.start);
      this.errorMarker = this.editor.getSession().addMarker(range, 'error-marker', 'line', true);
    }
  }

  $onDestroy() {
    this.editor?.destroy();
    this.editor = null;
  }

  handleBannerIcon() {
    if (this.showRenamingWarning) {
      return 'illo-mini-cal-field-warning';
    }

    if (this.isExploreish || this.isImportedDataset) {
      return 'icon-info-outline';
    }
  }

  handleBannerMessage() {
    if (this.showRenamingWarning) {
      return 'Renaming this field will break any charts or other calculated fields that reference it.';
    }
    if (this.isExploreish) {
      return `This field is local to the Exploration and doesn’t impact the underlying Report.`;
    }
    if (this.isImportedDataset) {
      return `This field is local to the Report and won't impact the source Dataset`;
    }
  }
}

const FormulaEditorComponent: angular.IComponentOptions = {
  bindings: {
    calc: '<',
    formulaSignature: '<',
    model: '<',
    viewName: '<',
    onChange: '&',
    onCompile: '&',
    showRenamingWarning: '<?',
    readOnly: '<?',
    isExploreish: '<?',
    isImportedDataset: '<?',
  },
  template: `
  <div class="editor-container" ng-class="{'editor-container--read-only': ctrl.readOnly}">
    <div class="editor" data-test="calc-field-editor"></div>
    <div
      ng-if="ctrl.showBanner"
      class="warning-banner"
      ng-class="{'is-visible': ctrl.showBanner}"
      data-test="local-calc-field-banner">
      <div class="banner-icon">
        <capra-icon [icon]="ctrl.handleBannerIcon()"></capra-icon>
      </div>
      <div class="banner-message" ng-bind-html="ctrl.handleBannerMessage()"></div>
    </div>
  </div>
  `,
  controller: FormulaEditorController,
  controllerAs: 'ctrl',
};

angular.module('mode.components').component('formulaEditor', FormulaEditorComponent);
